// Updated layout with logo above title and fixed button text/route - May 2024
import React, { useState, useEffect, useRef } from 'react';
import { FiChevronDown, FiFilter, FiGrid, FiList, FiSearch, FiX } from 'react-icons/fi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';

// Import the product interface and ProductCard component
import { ProductCard, Product } from './ProductCard';
import { getActiveProducts, FrontendProduct } from '../api/productApi';

// Extended product interface for additional display properties
interface ExtendedProduct extends Omit<FrontendProduct, 'sizeType'> {
  categories?: string[];
  isFeatured?: boolean;
  images?: string[];
  category?: string;
  size?: string;
  sizeType?: string; // Make sizeType optional to match our usage
  sizeDisplay?: string;
}

const ProductShowcase: React.FC = () => {
  // State variables for product display
  const [products, setProducts] = useState<ExtendedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState("All");
  const [selectedPriceRange, setSelectedPriceRange] = useState("All");
  const [visibleProductCount, setVisibleProductCount] = useState(8);
  
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  
  const filters = [
    { id: 'all', label: 'All' },
    { id: 'featured', label: 'Featured' },
    { id: 'new', label: 'New Arrivals' },
    { id: 'sale', label: 'Sale' }
  ];
  
  const brands = [
    { id: 'All', label: 'All Brands' },
    { id: 'Nike', label: 'Nike' },
    { id: 'Adidas', label: 'Adidas' },
    { id: 'Jordan', label: 'Jordan' },
    { id: 'New Balance', label: 'New Balance' },
    { id: 'Puma', label: 'Puma' }
  ];
  
  const priceRanges = [
    { id: 'All', label: 'All Prices' },
    { id: '0-100', label: 'Under $100' },
    { id: '100-200', label: '$100 - $200' },
    { id: '200-300', label: '$200 - $300' },
    { id: '300+', label: 'Over $300' }
  ];
  
  // Fetch products on component mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const data = await getActiveProducts();
        
        // Transform API products to match the expected format
        const formattedProducts = data.map(product => ({
          ...product,
          categories: determineCategoriesFromProduct(product),
          isFeatured: Boolean(product.collab) || product.price >= 500
        }));
        
        setProducts(formattedProducts);
        setError(null);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching products:", err);
        setError("Failed to load products");
        setLoading(false);
      }
    };
    
    fetchProducts();
    
    // Listen for product inventory changes (e.g., after successful checkout)
    const handleInventoryChange = () => {
      fetchProducts();
    };
    
    window.addEventListener('productInventoryChanged', handleInventoryChange);
    
    return () => {
      window.removeEventListener('productInventoryChanged', handleInventoryChange);
    };
  }, []);
  
  // Determine product categories based on attributes
  const determineCategoriesFromProduct = (product: FrontendProduct) => {
    const categories = [];
    
    // Add brand as category
    if (product.brand) {
      categories.push(product.brand.toLowerCase());
    }
    
    // Determine if featured
    if (product.collab || product.price >= 500) {
      categories.push('featured');
    }
    
    // Add lifestyle category (fallback)
    categories.push('lifestyle');
    
    // Determine if new arrival (all products for now)
    categories.push('new');
    
    return categories;
  };
  
  // Intersection Observer for animation triggers
  useEffect(() => {
    if (!sectionRef.current) return;
    
    let observer: IntersectionObserver | null = null;
    
    try {
      observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry && entry.isIntersecting) {
            setIsVisible(true);
            if (observer) {
              observer.disconnect();
            }
          }
        },
        { threshold: 0.1 }
      );
    
      observer.observe(sectionRef.current);
    } catch (error) {
      console.error("IntersectionObserver error:", error);
      // Fallback to showing the section
      setIsVisible(true);
    }
    
    return () => {
      if (observer) {
        try {
          observer.disconnect();
        } catch (error) {
          console.error("Error disconnecting observer:", error);
        }
      }
    };
  }, []);
  
  // Add cleanup for transitions
  useEffect(() => {
    // Function to handle page transition cleanup
    const handleBeforeUnload = () => {
      setLoading(false);
      setIsVisible(true); // Ensure visibility state is resolved
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Make sure to clean up any other state that might cause issues during transitions
      setLoading(false);
    };
  }, []);
  
  // Filter products based on selected options
  const filteredProducts = products.filter(product => {
    // Filter by category
    const matchesCategory = 
      activeFilter === 'all' || 
      (product.categories && product.categories.includes(activeFilter)) || 
      (activeFilter === 'featured' && product.isFeatured);
    
    // Filter by brand
    const matchesBrand = 
      selectedBrand === "All" || 
      product.brand === selectedBrand;
    
    // Filter by price range
    let matchesPriceRange = true;
    if (selectedPriceRange !== "All") {
      const [min, max] = selectedPriceRange.split('-').map(Number);
      if (max) {
        matchesPriceRange = product.price >= min && product.price <= max;
      } else {
        // For "300+" price range
        matchesPriceRange = product.price >= min;
      }
    }
    
    // Filter by search term
    const matchesSearch = 
      !searchTerm || 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      product.brand.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesBrand && matchesPriceRange && matchesSearch;
  });

  // Handle the filter click
  const handleFilterClick = (filter: string) => {
    setActiveFilter(filter);
  };
  
  // Handle load more products
  const handleLoadMore = () => {
    setVisibleProductCount(prev => prev + 8);
  };
  
  // Reset all filters
  const resetFilters = () => {
    setActiveFilter('all');
    setSelectedBrand("All");
    setSelectedPriceRange("All");
    setSearchTerm('');
  };

  return (
    <section ref={sectionRef} className="py-16 md:py-24 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 left-0 w-64 h-64 rounded-full bg-accent/5 blur-3xl"></div>
        <div className="absolute -bottom-20 right-0 w-64 h-64 rounded-full bg-accent/5 blur-3xl"></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div 
          className="mb-12 text-center transform transition-all duration-700"
          style={{ 
            transform: isVisible ? 'translateY(0)' : 'translateY(2rem)',
            opacity: isVisible ? 1 : 0 
          }}
        >
          <span className="inline-block text-accent text-sm font-medium tracking-wider mb-2">OUR COLLECTION</span>
          <div className="flex justify-center mb-6">
            <a href="/" className="text-3xl font-bold tracking-tighter hover:text-accent transition-colors">
              GRAILED804
            </a>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-primary mb-3">Our Entire Inventory</h2>
          <div className="w-24 h-1 bg-accent mx-auto mb-6"></div>
          <p className="text-secondary max-w-2xl mx-auto">Discover our entire inventory with rare, hard-to-find sneakers: Jordan Retros, Nike SBs, Collaborations, and more.</p>
        </div>
        
        {/* Filters and Search */}
        <div 
          className="mb-10 transform transition-all duration-700"
          style={{ 
            transform: isVisible ? 'translateY(0)' : 'translateY(2rem)',
            opacity: isVisible ? 1 : 0,
            transitionDelay: '150ms'
          }}
        >
          <div className="flex items-center space-x-2 overflow-x-auto scrollbar-hide py-2">
            {filters.map(filter => (
              <button
                key={filter.id}
                onClick={() => handleFilterClick(filter.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeFilter === filter.id
                    ? 'bg-accent text-white shadow-glow-sm' 
                    : 'bg-background-light text-secondary hover:bg-background-lighter'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Display */}
        <div className="min-h-[300px] relative">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <FontAwesomeIcon icon={faSpinner} spin className="text-accent text-3xl mb-4" />
                <p className="text-secondary animate-pulse">Loading amazing products...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center bg-red-500/10 p-6 rounded-lg max-w-md">
                <p className="text-red-500 mb-3">{error}</p>
                <button 
                  className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors"
                  onClick={() => window.location.reload()}
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="my-16 text-center">
              <div className="mb-4">
                <svg className="w-16 h-16 mx-auto text-secondary/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-primary mb-2">No products found</h3>
              <p className="text-secondary mb-6">Try adjusting your filters or search criteria</p>
              <button
                onClick={resetFilters}
                className="px-6 py-2 rounded-lg text-sm font-medium bg-accent text-white hover:bg-accent/90 transition-all duration-300 shadow-glow-sm"
              >
                Reset Filters
              </button>
            </div>
          ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {filteredProducts.slice(0, visibleProductCount).map((product) => (
                <div key={product.id} className="col-span-1">
                  <ProductCard
                    product={{
                      id: product.id,
                      name: product.name,
                      brand: product.brand,
                      price: product.price,
                      image: product.image || (product.images && product.images.length > 0 ? product.images[0] : ''),
                      sizes: product.sizes,
                      collab: product.collab,
                      condition: product.condition,
                      size: product.size,
                      category: product.category || (product.categories && product.categories.length > 0 ? product.categories[0] : undefined),
                      sizeType: 'M', // Add default size type for Men's shoes
                      sizeDisplay: product.size ? `${product.size}M` : undefined // Format size as "9M" for display
                    }}
                  />
                </div>
              ))}
            </div>
            
            {/* View Entire Inventory button */}
            {!loading && !error && filteredProducts.length > 0 && (
              <div 
                className="mt-16 text-center w-full"
                style={{ 
                  transitionDelay: '600ms',
                  transform: isVisible ? 'translateY(0)' : 'translateY(2rem)',
                  opacity: isVisible ? 1 : 0,
                  transition: 'all 700ms cubic-bezier(0.16, 1, 0.3, 1)'
                }}
              >
                <Link 
                  to="/inventory" 
                  className="inline-block px-8 py-4 bg-accent/10 hover:bg-accent/20 border border-accent text-white rounded-md transition-all duration-300 font-medium group shadow-glow-sm"
                >
                  <span className="flex items-center">
                    View All Inventory 
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </span>
                </Link>
              </div>
            )}
          </>
          )}
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase; 